/**
 * Document Export Hook
 * Handles DOCX generation and clipboard operations
 */

import { useCallback } from 'react';
import { useTranscriptionStore } from '../store/useTranscriptionStore';
import { exportToDocx, exportToSrt, copyToClipboard } from '../services/documentService';
import { ExportFormat } from '../types';

export function useDocumentExport() {
  const { addLog, finalTranscript, speakerMap, intelligence } = useTranscriptionStore();

  /**
   * Export transcript to specified format
   */
  const handleExport = useCallback(
    async (format: ExportFormat, outputFileName: string): Promise<void> => {
      if (!finalTranscript) {
        return;
      }

      if (format === 'clipboard') {
        try {
          await copyToClipboard(finalTranscript);
          addLog('Copied to clipboard.', 'success');
        } catch {
          addLog('Clipboard copy failed. Try selecting the text manually.', 'error');
        }
        return;
      }

      if (format === 'srt') {
        try {
          exportToSrt(finalTranscript, outputFileName);
          addLog('SRT subtitle file downloaded.', 'success');
        } catch (srtErr: unknown) {
          const msg = srtErr instanceof Error ? srtErr.message : 'Unknown error';
          addLog(`SRT export failed: ${msg}.`, 'error');
        }
        return;
      }

      addLog('Compiling the transcription report for DOCX export...', 'info');
      try {
        await exportToDocx({
          outputFileName,
          speakerMap,
          intelligence,
          finalTranscript,
        });
        addLog('DOCX export complete.', 'success');
      } catch (docxErr: unknown) {
        const msg = docxErr instanceof Error ? docxErr.message : 'Unknown error';
        addLog(`DOCX export failed: ${msg}. Try clipboard or SRT export instead.`, 'error');
      }
    },
    [finalTranscript, speakerMap, intelligence, addLog]
  );

  return { handleExport };
}
